/**
 * /dashboard/settings/coa — Settings → Accounting → Chart of accounts
 * (SOP §8 + §11.7).
 *
 * The chart of accounts is already seeded at tenant-register time via
 * EnsureDefaultChartOfAccounts (20 system accounts). HR-admin can add
 * child accounts on the existing /dashboard/accounting/* surface; the
 * Settings leaf is informational + a link out.
 *
 * Migration note (2026-06-01, Next.js → Vite Phase 3g): direct port of
 * frontend/src/app/(dashboard)/dashboard/settings/coa/page.tsx.
 * next/link → TanStack Router <Link to=…>.
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowRight, BookOpen } from "lucide-react";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/coa",
)({
  component: SettingsCoaRoute,
});

function SettingsCoaRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold">Chart of accounts</h2>
            <p className="text-sm text-muted-foreground">
              The 20 system accounts are seeded at tenant creation. Add
              child accounts inside Accounting.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BookOpen className="h-4 w-4 text-primary" />
                Open in Accounting
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Button asChild className="gap-1.5">
                <Link to="/app/$tenantSlug/accounting" params={{ tenantSlug }}>
                  Go to Accounting
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </SettingsShell>
    </>
  );
}
